import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomColorPicker from "../../../../components/shared/CustomColorPicker";
import { WATERMARK_COLOR_PRESETS } from "./watermarkSettings";
import "./WatermarkColorMenu.css";

/**
 * Floating watermark color menu (docks beside left panel, like presets).
 * Holds job color swatches + the standard custom color picker.
 */
const WatermarkColorMenu = ({
  isOpen = false,
  onClose = () => {},
  color = "#FFFFFF",
  onChange = () => {},
  onApply = () => {},
  anchorSelector = null,
  anchorTopSelector = null,
  anchorGap = 10,
  ignoreCloseSelectors = ["#wmc-color-trigger", ".wmc-select--color"],
}) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x: 380, y: 200 });

  const placeBesideAnchor = useCallback(() => {
    if (!anchorSelector) return;
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const topEl = anchorTopSelector
      ? document.querySelector(anchorTopSelector)
      : null;
    const topRect = topEl?.getBoundingClientRect?.() || rect;

    const menuWidth = 340;
    const menuHeight = 620;
    let x = rect.right + anchorGap;
    let y = topRect.top;

    const maxX = window.innerWidth - menuWidth - 12;
    const maxY =
      window.innerHeight - Math.min(menuHeight, window.innerHeight - 24);
    if (x > maxX) {
      x = Math.max(12, rect.left - menuWidth - anchorGap);
    }
    y = Math.max(12, Math.min(y, maxY));
    setPosition({ x, y });
  }, [anchorGap, anchorSelector, anchorTopSelector]);

  useEffect(() => {
    if (!isOpen) return undefined;
    placeBesideAnchor();
    const onResize = () => placeBesideAnchor();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen, placeBesideAnchor]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      for (const sel of ignoreCloseSelectors) {
        if (typeof sel === "string" && e.target.closest?.(sel)) return;
      }
      onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ignoreCloseSelectors, isOpen, onClose]);

  if (!isOpen) return null;

  const activeHex = String(color || "#FFFFFF").toUpperCase();

  return (
    <div
      className="wmc-color-menu"
      ref={menuRef}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      role="dialog"
      aria-label="Watermark color"
    >
      <div className="wmc-color-menu__header">
        <span className="wmc-color-menu__title">Color</span>
        <span className="wmc-color-menu__hint">Presets & custom</span>
      </div>

      <div
        className="wmc-color-menu__presets"
        role="listbox"
        aria-label="Popular watermark colors"
      >
        {WATERMARK_COLOR_PRESETS.map((hex) => {
          const selected = activeHex === hex.toUpperCase();
          const light =
            hex.toUpperCase() === "#FFFFFF" ||
            hex.toUpperCase() === "#F5F5F5" ||
            hex.toUpperCase() === "#E0E0E0";
          return (
            <button
              key={hex}
              type="button"
              role="option"
              aria-selected={selected}
              title={hex}
              className={`wmc-color-menu__swatch${selected ? " is-active" : ""}${
                light ? " is-light" : ""
              }`}
              style={{ background: hex }}
              onClick={() => {
                onChange(hex);
                onApply(hex);
                onClose();
              }}
            />
          );
        })}
      </div>

      <div className="wmc-color-menu__divider" aria-hidden="true" />

      <CustomColorPicker
        embedded
        isOpen
        showPopularPalette={false}
        showDragHeader={false}
        color={color}
        onChange={onChange}
        onApply={(c) => {
          onApply(c);
          onClose();
        }}
        applyLabel="Apply Color"
        onClose={onClose}
      />
    </div>
  );
};

export default WatermarkColorMenu;
